import { useState, type ReactElement, type ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@teach/ui'
import { useIsMobile } from '@/hooks/use-mobile'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  /** Элемент подзаголовка (уходит в `asChild`) — на него ссылается `aria-describedby`.
   *  Нет подзаголовка — нет и ссылки: `aria-describedby` снимается явно, иначе Radix
   *  ругается в консоль на окно без описания. */
  description?: ReactElement
  /** Ширина окна на ПК (`max-w-*`). На телефоне шторка всегда во всю ширину. */
  className?: string
  /** Фокус при открытии (Radix `onOpenAutoFocus`). По умолчанию — первое поле;
   *  `preventDefault` оставляет клавиатуру телефона закрытой. */
  onOpenAutoFocus?: (event: Event) => void
  children: ReactNode
}

/**
 * Окно, которое на телефоне становится шторкой снизу. Центрированное окно на
 * телефоне неудобно: высокое содержимое (витрина пакетов) не помещается, а
 * крестик в углу далеко от пальца. Шторка выезжает снизу, держит ручку-полоску
 * и прокручивается внутри себя, шапка с крестиком остаётся на месте.
 */
export const ResponsiveDialog = ({
  open,
  onOpenChange,
  title,
  description,
  className,
  onOpenAutoFocus,
  children,
}: Props) => {
  const live = useIsMobile()
  const noDescription = description ? {} : { 'aria-describedby': undefined }
  // Режим выбирается на открытии и держится, пока окно открыто: поворот телефона
  // пересекает 768 px (альбомный iPhone — 844–932), и смена окна на шторку
  // пересоздавала содержимое — в окне оплаты сбрасывались выбранный способ, ошибка
  // и блокировка повторного «Оплатить». Пока окно закрыто, режим идёт за шириной.
  const [isMobile, setIsMobile] = useState(live)
  if (!open && isMobile !== live) setIsMobile(live)
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="flex max-h-[90dvh] flex-col gap-0 rounded-t-3xl p-0 pb-[env(safe-area-inset-bottom,0px)]"
          onOpenAutoFocus={onOpenAutoFocus}
          {...noDescription}
        >
          <div
            aria-hidden="true"
            className="mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-foreground/20"
          />
          <SheetHeader className="shrink-0 px-5 pb-3 pt-3 text-left">
            <SheetTitle className="pr-8">{title}</SheetTitle>
            {description ? <SheetDescription asChild>{description}</SheetDescription> : null}
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6">{children}</div>
        </SheetContent>
      </Sheet>
    )
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={className} onOpenAutoFocus={onOpenAutoFocus} {...noDescription}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription asChild>{description}</DialogDescription> : null}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  )
}
